import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { listDashboardVideosPage, Video, getVideoRoute, isShortVideo } from '../../lib/services/videoService';
import VideoCard from './VideoCard';

interface ShortsPreviewProps {
  /** Show 3 larger cards instead of 4 */
  compactMode?: boolean;
}

export default function ShortsPreview({ compactMode = false }: ShortsPreviewProps) {
  const { t } = useTranslation('home');
  const [shorts, setShorts] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadShorts = async () => {
      try {
        const feed = await listDashboardVideosPage({ pageSize: 80 });
        const videos = feed.videos;
        // Filter for shorts: only 9:16 aspect ratio or type='short'
        const shortsOnly = videos.filter(v => isShortVideo(v));
        setShorts(shortsOnly.slice(0, compactMode ? 3 : 4));
      } catch (err) {
        console.error('Failed to load shorts:', err);
      } finally {
        setLoading(false);
      }
    };
    loadShorts();
  }, [compactMode]);

  if (loading) {
    return (
      <section className="mb-8" aria-labelledby="shorts-heading">
        <h2 id="shorts-heading" className="text-xl font-bold mb-4" style={{ color: 'var(--vb-accent)' }}>
          {t('sections.shorts')}
        </h2>
        <div className={`grid gap-4 ${compactMode ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-4'}`}>
          {Array.from({ length: compactMode ? 3 : 4 }).map((_, i) => (
            <div
              key={i}
              className="aspect-[9/16] rounded-xl animate-pulse"
              style={{ backgroundColor: 'var(--vb-bg-hover)' }}
            />
          ))}
        </div>
      </section>
    );
  }

  if (!shorts.length) {
    return null;
  }

  return (
    <section className="mb-8" aria-labelledby="shorts-heading">
      <div className="flex items-center justify-between mb-4">
        <h2 id="shorts-heading" className="text-xl font-bold" style={{ color: 'var(--vb-accent)' }}>
          {t('sections.shorts')}
        </h2>
        <button
          onClick={() => navigate('/shorts')}
          className="text-sm transition-colors hover:underline focus:outline-none focus:ring-2 focus:ring-yellow-400 rounded px-2 py-1"
          style={{ color: 'var(--vb-text-muted)' }}
        >
          {t('sections.viewAll')} →
        </button>
      </div>
      <div className={`grid gap-4 ${compactMode ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-4'}`}>
        {shorts.map((short) => (
          <VideoCard
            key={short.id}
            id={short.id}
            title={short.title}
            thumbnail={short.thumbnail}
            duration={short.duration}
            aspectRatio="short"
            href={getVideoRoute(short)}
            showDuration={false}
            status={short.status}
            source={short.source}
            youtubeId={short.youtubeId}
            streams={short.streams}
            downloadUrl={short.downloadUrl}
            rawUrl={short.rawUrl}
            channelName={short.channelName}

            creatorAvatar={short.creatorAvatar}
            timeAgo={short.timeAgo}
            viewCount={short.viewCount}
          />
        ))}
      </div>
    </section>
  );
}
